import { useMemo } from 'react';

import { Messages } from 'config';
import { mapQuery } from 'utils/query';
import { Flex } from 'components/flex';
import { List } from 'components/list';
import { Loader } from 'components/loader';
import { ListCard } from 'components/list-card';
import { ListHeader } from 'components/list-header';
import { DeleteDialog } from 'components/delete-dialog';
import { formatPrice, joinStrings } from 'utils/helpers';
import { DownloadButton } from 'components/specified-buttons';
import { CashCardType, ListCardStyle, AccountingExpense } from 'types';
import {
  useExpensesQuery,
  useDeleteExpenseMutation,
} from 'store/api/accounting';
import {
  isMobile,
  handleMutation,
  useSearchQuery,
  useDownloadFile,
} from 'hooks';

import { ExpensesProps } from '../types';
import { CashCards } from '../cash-cards';

import * as Styles from './styles';
import { EditExpense, CreateExpense } from './form';
import {
  getExpenseDate,
  formatExpenseDate,
  matchesExpenseSearch,
  getEventPaymentExpenseRows,
} from './helpers';

const template = [
  'minmax(110px, 0.8fr)',
  'minmax(92px, 0.6fr)',
  '120px',
  'minmax(120px, 1fr)',
  'minmax(120px, 1fr)',
  'minmax(120px, 1fr)',
  'minmax(120px, 1fr)',
  '104px',
].join(' ');
const titles = [
  'Category',
  'Date',
  'Amount',
  'Payment Method',
  'Notes',
  'Employees',
  'Created By',
];

const getEventAmount = (
  currentExpense: AccountingExpense,
  eventId: number
): number =>
  Number(
    currentExpense?.events?.find((item) => item?.eventId === eventId)?.amount
  ) || 0;

export const Expenses = ({ event }: ExpensesProps) => {
  const mobile = isMobile();
  const { debounceQuery, ...search } = useSearchQuery();

  const { data: accountingExpenses, ...accountingProps } = useExpensesQuery({
    search: debounceQuery,
    withReimbursement: true,
    eventIds: JSON.stringify([event.id]),
  });

  const { downloadFile } = useDownloadFile();

  const accountingExpenseRows = useMemo(
    () =>
      accountingExpenses?.map((expense) => ({
        id: expense.id,
        accountingExpense: expense,
        notes: expense.description,
        date: getExpenseDate(expense),
        receiptUrl: expense.receiptUrl,
        createdBy: expense.createdBy?.name,
        source: 'accounting-expense' as const,
        amount: getEventAmount(expense, event.id),
        paymentMethodName: expense.paymentMethod?.name,
        categoryName: joinStrings(
          expense.categories.map((category) => category.name),
          ','
        ),
        employeeName: joinStrings(
          expense.employees.map((employee) => employee?.user?.name),
          ','
        ),
      })) ?? [],
    [accountingExpenses, event.id]
  );

  const expenseRows = useMemo(
    () =>
      [
        ...accountingExpenseRows,
        ...getEventPaymentExpenseRows(event.payments),
      ].filter((row) => matchesExpenseSearch(row, debounceQuery)),
    [accountingExpenseRows, debounceQuery, event.payments]
  );

  const totalOut = useMemo(
    () => expenseRows.reduce((acc, value) => acc + value.amount, 0),
    [expenseRows]
  );

  const [remove, mutation] = useDeleteExpenseMutation();
  handleMutation({
    ...mutation,
    successMessage: `Expense ${Messages.DELETED_SUCCESSFULLY}`,
  });

  return (
    <Loader isFullScreen={false} isLoading={accountingProps.isLoading}>
      <Flex gap={24} fullHeight flexDirection={mobile ? 'column' : 'row'}>
        <CashCards
          showOnlyCash
          eventId={event.id}
          customTotalCash={totalOut}
          customType={CashCardType.Expenses}
        />

        <Flex gap={24} fullWidth fullHeight flexDirection="column">
          <List
            {...mapQuery(accountingProps)}
            {...search}
            count={expenseRows.length}
            mainWrapperStyles={{ padding: 0 }}
            searchContainerStyle={{ margin: '16px 0' }}
            searchBarRightSlot={<CreateExpense eventId={event.id} />}
            header={
              <ListHeader padding="8px 0" titles={titles} template={template} />
            }
            searchInputProps={{
              placeholder:
                'Search category, payment method, amount, notes or created by',
            }}
            footer={
              <ListCard
                selected
                padding="16px"
                template={template}
                even={ListCardStyle.Green}
                values={[
                  <p className="semibold">Total</p>,
                  formatPrice(totalOut),
                ]}
              />
            }
          >
            {expenseRows.map((value) => {
              const amount = formatPrice(value.amount);
              const isAccountingExpense = value.source === 'accounting-expense';
              const isReimbursement = String(value.id).startsWith(
                'reimbursement'
              );

              return (
                <ListCard
                  key={value.id}
                  padding="16px"
                  headers={titles}
                  template={template}
                  values={[
                    value.categoryName,
                    formatExpenseDate(value),
                    <Styles.Amount isNegative={value.amount < 0}>
                      {amount}
                    </Styles.Amount>,
                    value.paymentMethodName,
                    value.notes,
                    value.employeeName,
                    value.createdBy,
                    <Flex gap={16} justifyContent="flex-end">
                      {value.receiptUrl && (
                        <DownloadButton
                          url={value.receiptUrl}
                          onDownloadFile={downloadFile}
                        />
                      )}
                      {isAccountingExpense && !isReimbursement && (
                        <>
                          <EditExpense data={value.accountingExpense} />
                          <DeleteDialog
                            id={value.id}
                            action={remove}
                            title="Delete Expense"
                            name={`expense with ${amount}`}
                          />
                        </>
                      )}
                    </Flex>,
                  ]}
                />
              );
            })}
          </List>
        </Flex>
      </Flex>
    </Loader>
  );
};
